import React, {useEffect, useRef, useState} from "react";
import {engineHttp, onEngineEvent} from "../common/engineLink";

// What the projector shows over the page: the video a block's segment is playing. The engine plays its sound on
// the show clock and says when the picture started (stage-video); the stage keeps a muted picture in step with
// it — small drift is absorbed by a slight speed change, big drift (a stall, a late load) by a seek. A paused
// show holds the frame.
const useChase = (video, startedAt) => {
    useEffect(() => {
        if (startedAt == null) return;
        const t = setInterval(() => {
            const v = video.current;
            if (!v || v.readyState < 2) return;
            const want = (Date.now() - startedAt) / 1000, drift = v.currentTime - want;
            if (want < 0 || want > v.duration) return;
            if (Math.abs(drift) > 0.25) { v.currentTime = want; v.playbackRate = 1; }
            else v.playbackRate = Math.min(1.05, Math.max(0.95, 1 - drift * 0.5));
        }, 250);
        return () => clearInterval(t);
    }, [video, startedAt]);
};

const VideoLayer = ({file, startedAt, pausedAt}) => {
    const video = useRef(null);
    useEffect(() => {
        const v = video.current;
        if (!v) return;
        if (pausedAt != null) { v.pause(); v.currentTime = pausedAt; return; }
        v.currentTime = startedAt != null ? Math.max(0, (Date.now() - startedAt) / 1000) : 0;
        v.play().catch(() => {});
    }, [file, startedAt, pausedAt]);
    useChase(video, pausedAt == null ? startedAt : null);
    return <video ref={video} src={`${engineHttp()}/stage-media/${file}`} muted playsInline
        style={{position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', objectFit: 'contain'}}/>;
};

export const StageCompositor = () => {
    const [video, setVideo] = useState(null);
    useEffect(() => onEngineEvent(e => {
        if (e.type === 'stage-video') setVideo(e.video ? {file: `${e.video}.mp4`, startedAt: e.startedAt, pausedAt: e.pausedAt} : null);
    }), []);

    if (!video) return null;
    return <div style={{position: 'fixed', inset: 0, zIndex: 900, pointerEvents: 'none', overflow: 'hidden', background: '#000'}}>
        <VideoLayer key={`${video.file}:${video.startedAt ?? 'paused'}`} {...video}/>
    </div>;
};
